import { Employee, Partner, PartnerService } from '@geovelo-frontends/commons';
import { useQuery } from '@tanstack/react-query';

import { queryKeys } from './query-keys';

export interface IPartnerEmployeesSummary {
  employeesCount: number;
  inactiveEmployees: Employee[];
  joinedEmployeesCount: number;
}

async function fetchPartnerEmployeesSummary(partner: Partner): Promise<IPartnerEmployeesSummary> {
  const [{ count }, { count: joinedCount }, { count: inactiveEmployeesCount, employees }] =
    await Promise.all([
      PartnerService.getEmployees(partner, {
        statuses: ['joined', 'waitingUserToJoin'],
        page: 1,
        pageSize: 1,
        ordering: '-id',
        query: '{id}',
      }),
      PartnerService.getEmployees(partner, {
        statuses: ['joined'],
        page: 1,
        pageSize: 1,
        ordering: '-id',
        query: '{id}',
      }),
      PartnerService.getEmployees(partner, {
        statuses: ['waitingForApproval'],
        page: 1,
        pageSize: 100,
        ordering: '-id',
        query: '{id, user{username}, professional_email}',
      }),
    ]);

  const inactiveEmployees: Employee[] = [...employees];

  if (inactiveEmployeesCount > 100) {
    const results = await Promise.all(
      new Array(Math.ceil(inactiveEmployeesCount / 100) - 1).fill(null).map((_, index) =>
        PartnerService.getEmployees(partner, {
          statuses: ['waitingForApproval'],
          page: index + 2,
          pageSize: 100,
          ordering: '-id',
          query: '{id, user{username}, professional_email}',
        }),
      ),
    );

    inactiveEmployees.push(...results.flatMap(({ employees }) => employees));
  }

  return { employeesCount: count, joinedEmployeesCount: joinedCount, inactiveEmployees };
}

function usePartnerEmployeesSummaryQuery(
  partner: Partner | undefined | null,
  employee: Employee | undefined | null,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: queryKeys.employees.summary(partner?.id),
    queryFn: () => fetchPartnerEmployeesSummary(partner as Partner),
    enabled: !!partner && !!employee?.isAdminOrAnimator && enabled,
    staleTime: 60_000,
  });
}

export default usePartnerEmployeesSummaryQuery;
